import { useState, useEffect } from "react";
import { motion } from "framer-motion";

import { AppWrap, MotionWrap } from "../../wrapper";
import "./Consultancy.scss";

const Consultancy = () => {
  const services = [
    {
      title: "PCI DSS",
      cat: [
        {
          label: "What is PCI DSS?",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Introduction to PCI DSS</h2>
              <p>
                The{" "}
                <strong>
                  Payment Card Industry Data Security Standard (PCI DSS)
                </strong>
                is a global standard designed to ensure organizations that
                process, store, or transmit cardholder data maintain a secure
                environment.
              </p>
              <h3 className="text-xl font-semibold mt-4">Key Objectives</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Protect cardholder data</li>
                <li>Maintain a secure network</li>
                <li>Monitor and test security systems</li>
                <li>Maintain an information security policy</li>
              </ul>
            </div>
          ),
        },
        {
          label: "Penetration Testing",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">
                Penetration Testing Services
              </h2>
              <p>
                We simulate real-world attacks to uncover vulnerabilities in
                your systems, applications, and networks that could be exploited
                by malicious actors.
              </p>
              <h3 className="text-xl font-semibold">Our Approach</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Reconnaissance & threat modeling</li>
                <li>Exploitation of vulnerabilities</li>
                <li>Reporting & remediation guidance</li>
              </ol>
            </div>
          ),
        },
        {
          label: "Gap Analysis",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Gap Analysis</h2>
              <p>
                Our PCI DSS gap analysis identifies where your organization
                currently stands and what needs to be done to achieve full
                compliance.
              </p>
              <h3 className="text-xl font-semibold">Deliverables</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Compliance posture report</li>
                <li>Detailed remediation roadmap</li>
                <li>Priority recommendations</li>
              </ul>
            </div>
          ),
        },
        {
          label: "Implementation",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Implementation Support</h2>
              <p>
                We assist organizations in implementing PCI DSS controls such as
                encryption, multi-factor authentication, and secure system
                configurations.
              </p>
              <h3 className="text-xl font-semibold">Benefits</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Stronger customer trust</li>
                <li>Reduced security risks</li>
                <li>Preparedness for compliance audits</li>
              </ul>
            </div>
          ),
        },
        {
          label: "Assessment and Auditing",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Assessment & Auditing</h2>
              <p>
                We conduct end-to-end PCI DSS assessments and audits to validate
                compliance, identify risks, and ensure ongoing protection.
              </p>
              <h3 className="text-xl font-semibold">Audit Process</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Pre-audit review</li>
                <li>Evidence collection & testing</li>
                <li>Final compliance report</li>
              </ul>
            </div>
          ),
        },
        {
          label: "Consultancy Services",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Expert Consultancy</h2>
              <p>
                Our experienced consultants guide you step-by-step in aligning
                your organization with PCI DSS requirements.
              </p>
              <h3 className="text-xl font-semibold">Why Choose Us?</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Tailored advice for your environment</li>
                <li>Business-friendly solutions</li>
                <li>Proven track record of successful compliance</li>
              </ul>
            </div>
          ),
        },
      ],
    },
    {
      title: "NIST CSF",
      cat: [
        {
          label: "What is NIST CSF?",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">
                NIST Cybersecurity Framework
              </h2>
              <p>
                The <strong>NIST CSF</strong> is a flexible, risk-based approach
                to managing cybersecurity risks. It consists of five key
                functions: Identify, Protect, Detect, Respond, and Recover.
              </p>
              <h3 className="text-xl font-semibold">Key Benefits</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Improved cybersecurity posture</li>
                <li>Risk management alignment</li>
                <li>Industry-recognized framework</li>
              </ul>
            </div>
          ),
        },
        {
          label: "Implementation",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Implementation Services</h2>
              <p>
                We help integrate NIST CSF into your business processes,
                ensuring each function is aligned with your unique risks.
              </p>
              <h3 className="text-xl font-semibold">Steps We Cover</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Current state assessment</li>
                <li>Framework integration</li>
                <li>Ongoing monitoring</li>
              </ol>
            </div>
          ),
        },
        {
          label: "Assessment",
          body: (
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">Assessment Services</h2>
              <p>
                Our NIST CSF assessments benchmark your security maturity level
                and provide actionable steps for improvement.
              </p>
              <h3 className="text-xl font-semibold">Deliverables</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Maturity scoring</li>
                <li>Gap analysis report</li>
                <li>Risk prioritization</li>
              </ul>
            </div>
          ),
        },
        {
          label: "Consultancy Services",
          body: (
            <div className="space-y-6 text-white">
              <h2 className="text-2xl font-bold">Consultancy Services</h2>
              <p>
                Our consultants tailor NIST CSF guidance to your organization,
                enabling effective cybersecurity governance.
              </p>
              <h3 className="text-xl font-semibold">Our Expertise</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Deep knowledge of NIST standards</li>
                <li>Sector-specific expertise</li>
                <li>Hands-on implementation support</li>
              </ul>
            </div>
          ),
        },
      ],
    },
  ];

  const [selectedCat, setSelectedCat] = useState(null);

  // 🔒 Prevent background scroll when modal is open
  useEffect(() => {
    if (selectedCat) {
      document.body.style.overflow = "hidden"; // lock scroll
    } else {
      document.body.style.overflow = "auto"; // unlock scroll
    }

    return () => {
      document.body.style.overflow = "auto"; // cleanup just in case
    };
  }, [selectedCat]);

  return (
    <>
      <h2 className="head-text flex gap-2 !mb-5 md:whitespace-nowrap !flex-wrap !justify-evenly">
        <span>Consultancy</span>&<span>Services</span>
      </h2>

      <div className="app__profiles !gap-20">
        {services.map((item, index) => (
          <motion.div
            whileInView={{ opacity: 1 }}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.5, type: "tween" }}
            className="bg-[var(--primary-color)] !p-5 !px-[100px] !rounded-xl flex flex-col gap-5"
            key={item.title + index}
          >
            <h2
              className="bold-text !text-[var(--secondary-color)]"
              style={{ marginTop: 20 }}
            >
              {item.title}
            </h2>
            {item?.cat?.map((catItem, idx) => (
              <p
                key={idx}
                className="p-text hover:!text-[var(--secondary-color)] cursor-pointer"
                onClick={() => setSelectedCat(catItem)}
              >
                {catItem.label}
              </p>
            ))}
          </motion.div>
        ))}
      </div>

      {/* Modal */}
      {selectedCat && (
        <div className="fixed inset-0 !bg-black/60 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-[var(--primary-color)] !rounded-xl !p-8 max-w-2xl w-full shadow-lg overflow-y-auto max-h-[80vh]"
          >
            {selectedCat.body}
            <button
              className="!mt-6 !px-4 !py-2 bg-[var(--secondary-color)] text-white rounded-lg !cursor-pointer"
              onClick={() => setSelectedCat(null)}
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default AppWrap(
  MotionWrap(Consultancy, "app__about"),
  "consultancy",
  "app__whitebg"
);
